import React, { useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from './styles/theme';
import { AWSAuthProvider as AuthProvider, useAWSAuth as useAuth } from './contexts/AWSAuthContext';
import { ToastProvider, useToast } from './components/ui/Toast';
import { Navigation } from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import AuthGate from './components/AuthGate';
// Initialize scan testing mode (env / ?testing=true / localStorage)
import './utils/scanAccuracyTesting';
import {
  isSubscriptionTestModeEnabled,
  syncSubscriptionTestModeFromUrl,
} from './utils/subscriptionTestMode';
import HomePage from './pages/HomePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import OnboardingPage from './pages/OnboardingPage';
import ScanPage from './pages/ScanPage';
import ResultsIndexPage from './pages/ResultsIndexPage';
import ResultsDetailPage from './pages/ResultsDetailPage';
import DIYMasksPage from './pages/DIYMasksPage';
import DietPage from './pages/DietPage';
import FoodScanPage from './pages/FoodScanPage';
import ProgressPage from './pages/ProgressPage';
import PricingPage from './pages/PricingPage';
import ElitePage from './pages/ElitePage';
import PricingStrategyPage from './pages/PricingStrategyPage';
import ProfilePage from './pages/ProfilePage';
import ReferralPage from './pages/ReferralPage';
import SettingsPage from './pages/SettingsPage';
import ProductsPage from './pages/ProductsPage';
import DietPlanFoodsPage from './pages/DietPlanFoodsPage';
import DIYMasksDetailPage from './pages/DIYMasksDetailPage';
import { TestSubscriptionUpgrade } from './components/TestSubscriptionUpgrade';
import VIPChatPage from './pages/VIPChatPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import RekognitionTest from './components/RekognitionTest';

const AppContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

/* Account for fixed nav (72px) + iOS safe area (notch/Dynamic Island) */
const MainContent = styled.main`
  padding-top: calc(72px + env(safe-area-inset-top, 0px));
  min-height: calc(100vh - 72px - env(safe-area-inset-top, 0px));
  padding-bottom: calc(5rem + env(safe-area-inset-bottom, 0px));
`;

const FooterBar = styled.footer`
  border-top: 1px solid ${theme.colors.border.light};
  padding: 1.25rem 1rem;
  padding-bottom: max(1.25rem, env(safe-area-inset-bottom, 0px));
  background: #ffffff;
  display: flex;
  justify-content: center;
`;

const FooterLink = styled(Link)`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.primary[500]};
  }
`;

const AppContent: React.FC = () => {
  const { showToast } = useToast();

  useEffect(() => {
    syncSubscriptionTestModeFromUrl();
    if (process.env.REACT_APP_TESTING_MODE === 'true') {
      import('./utils/scanAccuracyTesting').then(({ enableTestingMode }) => enableTestingMode());
    }
  }, []);

  // Handle pending OAuth redirect (after Cognito logout clears session cookies)
  useEffect(() => {
    const pendingOAuthUrl = sessionStorage.getItem('glowlens_pending_oauth_url');
    if (pendingOAuthUrl) {
      sessionStorage.removeItem('glowlens_pending_oauth_url');
      window.location.href = pendingOAuthUrl;
    }
  }, []);

  return (
    <AuthProvider onError={(message, type) => showToast(message, type === 'error' ? 'error' : type === 'info' ? 'info' : 'success')}>
      <AppContainer>
        <Navigation />
        <MainContent>
          <Routes>
            <Route path="/" element={<AuthGate><HomePage /></AuthGate>} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/callback" element={<OAuthCallbackPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/scan" element={<ProtectedRoute><ScanPage /></ProtectedRoute>} />
            <Route path="/results" element={<ProtectedRoute><ResultsIndexPage /></ProtectedRoute>} />
            <Route path="/results/:scanId" element={<ProtectedRoute><ResultsDetailPage /></ProtectedRoute>} />
            <Route path="/diy-masks" element={<ProtectedRoute><DIYMasksPage /></ProtectedRoute>} />
            <Route path="/diet" element={<ProtectedRoute><DietPage /></ProtectedRoute>} />
            <Route path="/food-scan" element={<ProtectedRoute><FoodScanPage /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/elite" element={<ElitePage />} />
            <Route path="/pricing-strategy" element={<PricingStrategyPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/diet-plan-foods" element={<ProtectedRoute><DietPlanFoodsPage /></ProtectedRoute>} />
            <Route path="/diy-masks-detail" element={<ProtectedRoute><DIYMasksDetailPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/vip-chat" element={<ProtectedRoute><VIPChatPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/referral" element={<ReferralPage />} />
            <Route path="/test-rekognition" element={<ProtectedRoute><RekognitionTest /></ProtectedRoute>} />
          </Routes>
        </MainContent>
        <FooterBar>
          <FooterLink to="/results">Results</FooterLink>
        </FooterBar>
        
        {/* Test Subscription Upgrade — REACT_APP_ENABLE_TEST_MODE, ?test_mode=true, or localStorage */}
        {isSubscriptionTestModeEnabled() && <TestSubscriptionUpgrade />}
      </AppContainer>
    </AuthProvider>
  );
};

function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

export default App;
